import { PluginWidget as PluginWidgetType } from '../types';
import { useApi } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  useStarredEntities,
} from '@backstage/plugin-catalog-react';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { Progress, Link as BackstageLink } from '@backstage/core-components';
import { Star } from 'lucide-react';
import useAsync from 'react-use/lib/useAsync';

const FavoritesWidget = ({ plugin }: { plugin: PluginWidgetType }) => {
  const title = plugin.title || '⭐ Favorites';
  const catalogApi = useApi(catalogApiRef);
  const { starredEntities, toggleStarredEntity } = useStarredEntities();

  const { value: entities, loading, error } = useAsync(async () => {
    if (!starredEntities.size) {
      return [];
    }

    const result = await catalogApi.getEntitiesByRefs({
      entityRefs: [...starredEntities],
      fields: [
        'kind',
        'metadata.namespace',
        'metadata.name',
        'spec.type',
        'metadata.title',
        'spec.profile.displayName',
      ],
    });

    return result.items.filter((e): e is Entity => !!e);
  }, [catalogApi, starredEntities]);

  const getEntityUrl = (entity: Entity) => {
    const kind = entity.kind || 'component';
    const namespace = entity.metadata.namespace || 'default';
    const name = entity.metadata.name;
    return `/catalog/${namespace}/${kind.toLowerCase()}/${name}`;
  };

  return (
    <div className="bg-[var(--appvia-background-paper)] rounded-lg shadow-[var(--appvia-shadow-2)] p-6 border border-[var(--appvia-divider)]">
      <h2 className="text-xl font-semibold mb-4 text-[var(--appvia-text-primary)]">
        {title}
      </h2>

      {starredEntities.size === 0 && (
        <p className="text-[var(--appvia-text-secondary)] text-sm">
          {plugin.config?.emptyMessage ||
            'Click the star beside an entity name to add it to this list!'}
        </p>
      )}

      {starredEntities.size > 0 && loading && (
        <div className="flex justify-center py-4">
          <Progress />
        </div>
      )}

      {error && (
        <div className="text-red-500 text-sm">
          Error loading starred entities: {error.message}
        </div>
      )}

      {entities && entities.length > 0 && (
        <div className="space-y-2">
          {entities
            .sort((a, b) =>
              (a.metadata.title ?? a.metadata.name).localeCompare(
                b.metadata.title ?? b.metadata.name,
              ),
            )
            .map(entity => {
              const displayName = entity.metadata.title ?? entity.metadata.name;
              const kind = entity.kind || 'component';
              const entityType = entity.spec?.type
                ? `${kind.toLowerCase()} — ${(
                    entity.spec as any
                  ).type.toLowerCase()}`
                : kind.toLowerCase();

              return (
                <div
                  key={stringifyEntityRef(entity)}
                  className="flex items-center gap-2 p-2 hover:bg-[var(--appvia-background-default)] rounded transition-colors"
                >
                  <button
                    onClick={() => toggleStarredEntity(entity)}
                    className="flex-shrink-0 text-yellow-500 hover:text-yellow-600 transition-colors"
                    aria-label="Remove from favorites"
                    title="Remove from favorites"
                  >
                    <Star className="w-5 h-5 fill-current" />
                  </button>
                  <BackstageLink
                    to={getEntityUrl(entity)}
                    className="flex-1 min-w-0"
                  >
                    <div className="truncate text-[var(--appvia-text-primary)] font-medium">
                      {displayName}
                    </div>
                    <div className="text-xs text-[var(--appvia-text-secondary)] uppercase">
                      {entityType}
                    </div>
                  </BackstageLink>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};

interface PluginWidgetProps {
  plugin: PluginWidgetType;
}

/**
 * Currently supports:
 * - favorites: Displays user's starred catalog entities
 */
export const PluginWidget = ({ plugin }: PluginWidgetProps) => {
  if (!plugin.enabled) {
    return null;
  }

  // Render built-in widgets based on ID
  switch (plugin.id) {
    case 'favorites':
      return <FavoritesWidget plugin={plugin} />;
    default:
      // Unknown widget ID - ignore it (third-party widgets use extension system)
      return null;
  }
};
